import type { Column } from '@tanstack/react-table';
import { PlusCircleIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../badge';
import { Button } from '../button';
import { Input } from '../../forms/input';
import { Popover, PopoverContent, PopoverTrigger } from '../popover';
import { Separator } from '../separator';

export type NumberRange = { min?: number; max?: number };

function formatRange({ min, max }: NumberRange) {
  if (min != null && max != null) {
    return `${min} – ${max}`;
  }
  return min != null ? `≥ ${min}` : `≤ ${max}`;
}

interface DataTableNumberFilterProps<TData, TValue> {
  column?: Column<TData, TValue>;
  title: string;
}

export function DataTableNumberFilter<TData, TValue>({
  column,
  title,
}: DataTableNumberFilterProps<TData, TValue>) {
  const { t } = useTranslation();
  const range = (column?.getFilterValue() as NumberRange | undefined) ?? {};
  const hasValue = range.min != null || range.max != null;

  const setBound = (bound: 'min' | 'max', raw: string) => {
    const next = { ...range, [bound]: raw === '' ? undefined : Number(raw) };
    column?.setFilterValue(next.min == null && next.max == null ? undefined : next);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-9 border-dashed">
          <PlusCircleIcon className="mr-2 size-4" />
          {title}
          {hasValue && (
            <>
              <Separator orientation="vertical" className="mx-2 h-4" />
              <Badge variant="secondary" className="rounded-sm px-1 font-normal">
                {formatRange(range)}
              </Badge>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 space-y-2 p-3" align="start">
        <div className="flex items-center gap-2">
          <Input
            type="number"
            placeholder={t('dataTable.min')}
            value={range.min ?? ''}
            onChange={event => setBound('min', event.target.value)}
          />
          <span className="text-muted-foreground">–</span>
          <Input
            type="number"
            placeholder={t('dataTable.max')}
            value={range.max ?? ''}
            onChange={event => setBound('max', event.target.value)}
          />
        </div>
        {hasValue && (
          <Button
            variant="ghost"
            size="sm"
            className="w-full"
            onClick={() => column?.setFilterValue(undefined)}
          >
            {t('dataTable.clearFilters')}
          </Button>
        )}
      </PopoverContent>
    </Popover>
  );
}
